/**
 * Timers and requests behind the interpreter's wrap-up (see wrapUp.ts).
 *
 * Native keeps the wrap-up in Redux: the breather and "Take 5" timers live
 * here, outside any screen, so switching tabs cannot lose the return to the
 * queue. The web conference page only hands a record to the dashboard it is
 * about to load; the dashboard runs the timing itself.
 */
import { z } from 'zod';
import { getWhitelabelConfig } from '../base/whitelabel/functions';
import { mobileLog } from '../mobile/navigation/logging';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
import { apiClient } from '../shared/api-client';
import { getPersistentItem, getPersistentJson } from '../vrs-auth/storage';
import { interpreterWrapUpUpdated } from './actions';
import { queueService } from './InterpreterQueueService';
import { getDefaultInterpreterLanguages } from './interpreterLanguages';
import { TAKE_FIVE_MS, breatherReturnAt, startWrapUp, toWrapUpRecord, wrapUpTiming, writeBrowserWrapUpRecord } from './wrapUp';
// The call row is completed by the server's call_end; give it a moment so
// "7th today" counts the call that just finished.
const STATS_DELAY_MS = 1500;
const breakStartedSchema = z.object({ break: z.object({ id: z.string().min(1).max(120) }) });
const statsSchema = z.object({ todayCalls: z.number().int().nonnegative() });
let returnTimer;
let statsTimer;
// The queue socket's latest queue_status.eligibleQueueSize: waiting requests
// this interpreter would be offered once Available that nobody else holds an
// offer for (vrs-server/lib/state.js). Kept through the call so a client
// already waiting when it ends shortens the breather from the start;
// forgotten when that socket closes or the interpreter signs out, so a count
// from a socket that is gone never shortens a breather.
let lastEligibleQueueSize = 0;
/**
 * The breather lengths (whitelabel operations.wrapUpBreatherMs /
 * wrapUpClientWaitingMs). On native this is the app build's brand config
 * (malkaterp.json for Malka Terp), not the web host's (AGENTS.md).
 */
function tenantWrapUpTiming() {
    return wrapUpTiming(getWhitelabelConfig()?.operations);
}
function clearReturnTimer() {
    if (returnTimer !== undefined) {
        clearTimeout(returnTimer);
        returnTimer = undefined;
    }
}
function clearWrapUpTimers() {
    clearReturnTimer();
    if (statsTimer !== undefined) {
        clearTimeout(statsTimer);
        statsTimer = undefined;
    }
}
function currentWrapUp(store) {
    return store.getState()['features/interpreter-queue']?.wrapUp;
}
export function signedInInterpreterId() {
    if (getPersistentItem('vrs_user_role') !== 'interpreter') {
        return null;
    }
    const id = getPersistentJson('vrs_user_info')?.id;
    return typeof id === 'string' && id ? id : null;
}
/** The same wrap-up for the same signed-in account: a new call, a choice or a sign-out wins. */
function stillCurrent(store, expected) {
    const current = currentWrapUp(store);
    return Boolean(current && current.endedAt === expected.endedAt && current.userId === expected.userId
        && signedInInterpreterId() === expected.userId);
}
function update(store, wrapUp) {
    store.dispatch(interpreterWrapUpUpdated(wrapUp));
}
function interpreterProfile() {
    const profile = getPersistentJson('vrs_user_info');
    const strings = (value) => Array.isArray(value) && value.length > 0
        && value.every(item => typeof item === 'string' && item.trim().length > 0) ? value : undefined;
    return {
        languages: strings(profile?.languages) || getDefaultInterpreterLanguages(),
        name: typeof profile?.name === 'string' ? profile.name : undefined,
        serviceModes: strings(profile?.serviceModes)
    };
}
function announceAvailable() {
    const { languages, name, serviceModes } = interpreterProfile();
    queueService.updateInterpreterStatus('active', name, languages, serviceModes);
}
/**
 * The server already has the interpreter out of the queue after a call; this
 * makes it their stated choice, so no reconnect can put them back.
 */
function announcePaused() {
    if (queueService.getLastInterpreterStatus() === 'active') {
        queueService.updateInterpreterStatus('inactive', interpreterProfile().name, []);
    }
}
/** vrs_interpreter_breather_returned: how, and how long after the call, a breather ended. */
function reportBreatherReturned(wrapUp, reason) {
    emitMobileTelemetryEvent('vrs_interpreter_breather_returned', {
        breatherMs: Math.max(0, Date.now() - wrapUp.endedAt),
        reason
    }, { callId: wrapUp.callId, state: reason });
}
async function returnToQueue(store, expected, reason) {
    const current = currentWrapUp(store);
    if (!current || !stillCurrent(store, expected) || current.phase === 'paused' || current.busy) {
        return;
    }
    clearReturnTimer();
    if (current.phase === 'breather') {
        reportBreatherReturned(current, reason || (current.clientWaiting ? 'client_waiting' : 'timer'));
    }
    if (current.phase === 'break' && current.breakId) {
        update(store, { ...current, busy: true, error: undefined });
        const response = await apiClient.post(`/api/interpreter/breaks/${encodeURIComponent(current.breakId)}/end`, {}, { retryAuth: false });
        const latest = currentWrapUp(store);
        if (!latest || !stillCurrent(store, current)) {
            return;
        }
        // 404: the break was already ended somewhere else. Carry on.
        if ((response.error || response.status < 200 || response.status >= 300) && response.status !== 404) {
            mobileLog('warn', 'interpreter_wrap_up_break_end_failed', { status: response.status });
            update(store, { ...latest, busy: false, error: 'Your break could not be ended. Try again.' });
            return;
        }
    }
    clearWrapUpTimers();
    update(store, undefined);
    announceAvailable();
    mobileLog('info', 'interpreter_wrap_up_returned', { phase: current.phase });
}
function scheduleReturn(store, wrapUp) {
    clearReturnTimer();
    if (wrapUp.phase === 'paused' || typeof wrapUp.returnAt !== 'number') {
        return;
    }
    returnTimer = setTimeout(() => {
        returnTimer = undefined;
        void returnToQueue(store, wrapUp);
    }, Math.max(0, wrapUp.returnAt - Date.now()));
}
function scheduleTodayCount(store, wrapUp) {
    if (statsTimer !== undefined) {
        clearTimeout(statsTimer);
    }
    statsTimer = setTimeout(() => {
        statsTimer = undefined;
        void apiClient.get('/api/interpreter/stats', { retryAuth: false }).then(response => {
            const parsed = statsSchema.safeParse(response.data);
            const current = currentWrapUp(store);
            if (!response.error && parsed.success && current && stillCurrent(store, wrapUp)) {
                update(store, { ...current, todayCalls: parsed.data.todayCalls });
            }
        })
            .catch(() => undefined);
    }, STATS_DELAY_MS);
}
/**
 * An interpreted call just ended. Called by the queue middleware before the
 * call state is cleared, on every exit path.
 */
export function beginInterpreterWrapUp(queueStore, activeCall, { native }) {
    const userId = signedInInterpreterId();
    const { dispatch, getState } = queueStore;
    if (!userId || typeof getState !== 'function') {
        return;
    }
    const store = { dispatch, getState };
    const pauseAfterCall = Boolean(store.getState()['features/interpreter-queue']?.pauseAfterCall);
    const now = Date.now();
    if (!native) {
        // The dashboard knows whether this tab was taking queue calls.
        writeBrowserWrapUpRecord(toWrapUpRecord(startWrapUp({ activeCall, now, pauseAfterCall, userId, wasAvailable: true }), pauseAfterCall));
        return;
    }
    const wrapUp = startWrapUp({
        activeCall,
        clientWaiting: lastEligibleQueueSize > 0,
        now,
        pauseAfterCall,
        timing: tenantWrapUpTiming(),
        userId,
        wasAvailable: queueService.getLastInterpreterStatus() === 'active'
    });
    clearWrapUpTimers();
    if (wrapUp.phase === 'breather') {
        queueService.holdAvailabilityReplay();
    }
    else {
        announcePaused();
    }
    update(store, wrapUp);
    scheduleReturn(store, wrapUp);
    scheduleTodayCount(store, wrapUp);
    mobileLog('info', 'interpreter_wrap_up_started', {
        clientWaiting: Boolean(wrapUp.clientWaiting), pauseAfterCall, phase: wrapUp.phase
    });
}
/** The breather follows whether a client is waiting for this interpreter. */
function applyClientWaiting(queueStore, waiting) {
    const { dispatch, getState } = queueStore;
    if (typeof getState !== 'function') {
        return;
    }
    const store = { dispatch, getState };
    const current = currentWrapUp(store);
    if (!current || current.phase !== 'breather' || current.busy || !stillCurrent(store, current)
        || waiting === Boolean(current.clientWaiting)) {
        return;
    }
    const clientWaitingSince = waiting ? Date.now() : undefined;
    const next = {
        ...current,
        clientWaiting: waiting || undefined,
        clientWaitingSince,
        returnAt: breatherReturnAt({ clientWaiting: waiting, clientWaitingSince, endedAt: current.endedAt }, tenantWrapUpTiming())
    };
    update(store, next);
    scheduleReturn(store, next);
    mobileLog('info', 'interpreter_wrap_up_client_waiting', { waiting });
}
/**
 * Every interpreter queue_status (queue middleware). Only eligibleQueueSize
 * counts: queueSize also counts other languages, service modes and offers
 * another interpreter holds. No eligible count (not known yet, an older
 * server) changes nothing. While the breather runs, a waiting client ends it
 * `clientWaitingMs` after the call (3 s by default) once the card has said so;
 * once nobody is waiting the full breather applies again. Stay paused and
 * Take 5 leave the breather, so they always win.
 */
export function noteWrapUpQueueStatus(queueStore, eligibleQueueSize) {
    if (typeof eligibleQueueSize !== 'number' || !Number.isFinite(eligibleQueueSize) || eligibleQueueSize < 0) {
        return;
    }
    lastEligibleQueueSize = eligibleQueueSize;
    applyClientWaiting(queueStore, eligibleQueueSize > 0);
}
/**
 * The queue socket closed (a drop, or superseded by another sign-in) or the
 * interpreter signed out: its count is gone with it. A breather it shortened
 * gets its full length back, visibly, until a new socket reports again.
 */
export function forgetWrapUpQueueCount(queueStore) {
    lastEligibleQueueSize = 0;
    if (queueStore) {
        applyClientWaiting(queueStore, false);
    }
}
/**
 * [Take the call now] / [Go available now] on the breather card: back to
 * Available at once. The label follows whether a client is waiting, and so
 * does the reported reason.
 */
export function takeCallNowAfterCall() {
    return (dispatch, getState) => {
        const store = { dispatch, getState };
        const current = currentWrapUp(store);
        if (!current || current.busy || current.phase !== 'breather' || !stillCurrent(store, current)) {
            return;
        }
        return returnToQueue(store, current, current.clientWaiting ? 'take_now' : 'go_available');
    };
}
/** [Stay paused]: no return to the queue until the interpreter chooses Available. */
export function stayPausedAfterCall() {
    return (dispatch, getState) => {
        const store = { dispatch, getState };
        const current = currentWrapUp(store);
        if (!current || current.busy || current.phase !== 'breather' || !stillCurrent(store, current)) {
            return;
        }
        clearReturnTimer();
        update(store, { ...current, clientWaiting: undefined, clientWaitingSince: undefined, error: undefined,
            phase: 'paused', returnAt: undefined });
        announcePaused();
    };
}
/** [Take 5]: a recorded break that brings the interpreter back to Available by itself. */
export function takeFiveAfterCall() {
    return async (dispatch, getState) => {
        const store = { dispatch, getState };
        const current = currentWrapUp(store);
        if (!current || current.busy || current.phase === 'break' || !stillCurrent(store, current)) {
            return;
        }
        // The interpreter asked not to go straight back: stop the breather first.
        clearReturnTimer();
        update(store, { ...current, busy: true, clientWaiting: undefined, clientWaitingSince: undefined, error: undefined,
            returnAt: undefined });
        const response = await apiClient.post('/api/interpreter/breaks/start', { breakType: 'rest', paid: false }, { retryAuth: false });
        const latest = currentWrapUp(store);
        if (!latest || !stillCurrent(store, current)) {
            return;
        }
        const parsed = breakStartedSchema.safeParse(response.data);
        if (response.error || response.status < 200 || response.status >= 300 || !parsed.success) {
            // Fail safe: not back in the queue, and the card says why.
            mobileLog('warn', 'interpreter_wrap_up_break_start_failed', { status: response.status });
            update(store, {
                ...latest,
                busy: false,
                error: response.status === 409
                    ? 'A break is already running. End it from Break when you are ready.'
                    : 'The break could not start. You are paused.',
                phase: 'paused',
                returnAt: undefined
            });
            announcePaused();
            return;
        }
        const onBreak = {
            ...latest,
            breakId: parsed.data.break.id,
            busy: false,
            error: undefined,
            phase: 'break',
            returnAt: Date.now() + TAKE_FIVE_MS
        };
        update(store, onBreak);
        scheduleReturn(store, onBreak);
    };
}
/** [End break now] during Take 5, or [Try again] after an end failed. */
export function endWrapUpBreakNow() {
    return (dispatch, getState) => {
        const store = { dispatch, getState };
        const current = currentWrapUp(store);
        if (current?.phase === 'break') {
            return returnToQueue(store, current);
        }
    };
}
/** The interpreter made their own availability choice; the wrap-up is over. */
export function dismissInterpreterWrapUp() {
    return (dispatch, getState) => {
        if (!currentWrapUp({ dispatch, getState })) {
            return;
        }
        clearWrapUpTimers();
        dispatch(interpreterWrapUpUpdated(undefined));
    };
}
